import {
    MaintenanceJobAssignment,
    MaintenanceTask,
    MaintenanceTaskType,
} from 'aos-services/src/services/tasks/types/task/MaintenanceTask'
import { TaskTemplate } from 'aos-services/src/services/tasks/types/task/TaskTemplate'
import { Box } from 'aos-ui/src/components/base/Box'
import { Text } from 'aos-ui/src/components/base/Text'
import { ThemeVariant } from 'aos-ui/src/components/base/ThemeVariant'
import { PropsListEntry } from 'aos-ui/src/components/list/PropsList'
import { Color } from 'aos-ui-common/src/styles/Color'
import { colors } from 'aos-ui-universal'
import React, { FC } from 'react'
import { useTranslation } from 'react-i18next'
import styled, { css } from 'styled-components'

export const maintenanceTaskPropertyList = (
    t: MaintenanceTask | TaskTemplate<MaintenanceTask>,
    variant = ThemeVariant.White,
): PropsListEntry[] => [
    [
        'tasks.task-preview.maintenance.assignments',
        !!t.assignments?.length && (
            <Box>
                {t.assignments.map((a, index) => (
                    <Wrapper
                        paddingTop={8}
                        key={index}
                        isFirst={index === 0}
                        isLast={index === t.assignments!.length - 1}
                    >
                        <Text
                            as='span'
                            size={13}
                            weight='regular'
                            color={
                                variant === ThemeVariant.Black
                                    ? Color.TextPrimary
                                    : Color.TextDarkPrimary
                            }
                        >
                            {a.job?.label}
                        </Text>
                        {a.job?.jobType === MaintenanceTaskType.Brushes && (
                            <MaintenanceJobAssignmentDetails assignment={a} theme={variant} />
                        )}
                    </Wrapper>
                ))}
            </Box>
        ),
    ],
]

interface MaintenanceJobAssignmentDetailsProps {
    assignment: MaintenanceJobAssignment
    theme: ThemeVariant
}

export const MaintenanceJobAssignmentDetails: FC<MaintenanceJobAssignmentDetailsProps> = ({
    assignment,
    theme,
}) => {
    const { t: translate } = useTranslation()
    return (
        <Box paddingTop={8}>
            <AssignmentDetail>
                <DetailLabel
                    theme={theme}
                    value={`${translate('task-preview.maintenance.assignments.brushes-vehicle', {
                        ns: 'tasks',
                    })}:`}
                />
                <Box paddingLeft={4}>{assignment.brushesVehicle.label}</Box>
            </AssignmentDetail>
            <AssignmentDetail>
                <DetailLabel
                    theme={theme}
                    value={`${translate('task-preview.maintenance.assignments.brushes-brand', {
                        ns: 'tasks',
                    })}:`}
                />
                <Box paddingLeft={4}>{assignment.brushesBrand.name}</Box>
            </AssignmentDetail>
            <AssignmentDetail>
                <DetailLabel
                    theme={theme}
                    value={`${translate(
                        'task-preview.maintenance.assignments.engine-last-shift-hours',
                        { ns: 'tasks' },
                    )}:`}
                />
                <Box paddingLeft={4}>{assignment.engineLastShiftHours}</Box>
            </AssignmentDetail>
            <AssignmentDetail>
                <DetailLabel
                    theme={theme}
                    value={`${translate(
                        'task-preview.maintenance.assignments.brushes-last-shift-hours',
                        { ns: 'tasks' },
                    )}:`}
                />
                <Box paddingLeft={4}>{assignment.brushesLastShiftHours}</Box>
            </AssignmentDetail>
            <AssignmentDetail>
                <DetailLabel
                    theme={theme}
                    value={`${translate('task-preview.maintenance.assignments.total-hours', {
                        ns: 'tasks',
                    })}:`}
                />
                <Box paddingLeft={4}>{assignment.totalHours}</Box>
            </AssignmentDetail>
        </Box>
    )
}

const DetailLabel: FC<{ value: string; theme: ThemeVariant }> = ({ value, theme }) => (
    <Text
        as='span'
        size={12}
        weight='light'
        color={theme === ThemeVariant.Black ? colors['aos-text'].muted : Color.TextDarkSecondary}
    >
        {value}
    </Text>
)

const AssignmentDetail = styled(Box)`
    display: flex;
    flex-direction: row;
    align-items: center;
    padding-bottom: 8px;
    justify-content: space-between;
`

const Wrapper = styled(Box)<{ isLast: boolean; isFirst: boolean }>`
    ${p =>
        !p.isLast &&
        css`
            padding-bottom: 8px;
            border-bottom: 1px solid ${colors.border.subtle};
        `}
    ${p =>
        !p.isFirst &&
        css`
            padding-top: 16px;
        `}
`
